import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ExternalLink } from 'lucide-react'
import React, { useEffect, useState } from 'react'

import { DisplayTextOnHoverTooltip } from 'components/brokers/needs_attention/NeedsAttentionTable'
import TanstackTable, { type PaginationMetadata } from 'components/reusable/table/TanstackTable'
import { adminRecordInspector } from 'rails_routes'
import { Badge, CollapsibleContainer } from 'slab'
import { formatMoney, getLocaleDateString } from 'utils'
import { useGetAdminSubstantiationsSubstantiationIdRejectedSubstantiations } from 'utils/web_api'
import { type AdminSubstantiation } from 'utils/web_api.schemas'

import { PaymentMethodBadge } from '../PaymentMethodBadge'

interface RejectedSubstantiationsTableProps {
  substantiationId: string
}

const columnHelper = createColumnHelper<AdminSubstantiation>()
const getColumns = [
  columnHelper.accessor('id', {
    header: 'ID',
    cell: (info) => {
      const id = info.getValue()
      return (
        <Badge
          onActionClick={() => window.open(adminRecordInspector.show({ id }))}
          actionIcon={ExternalLink}
          actionLabel='Inspect'
          type='neutral'
        >
          {id.slice(-6)}
        </Badge>
      )
    },
  }),
  columnHelper.accessor('amount', {
    header: 'Amount',
    cell: (info) => {
      const amount = info.getValue()
      // BrokersMoney type has optional properties (value?: string, currency?: string)
      // Need to safely handle cases where amount is null/undefined or value is undefined
      return formatMoney(typeof amount?.value === 'string' ? parseFloat(amount.value) : amount?.value || 0)
    },
  }),
  columnHelper.accessor('payment_method', {
    header: 'Method',
    cell: (info) => <PaymentMethodBadge paymentMethod={info.getValue() || ''} />,
  }),
  columnHelper.accessor('created_at', {
    header: 'Created',
    cell: (info) => {
      const value = info.getValue()
      return value ? getLocaleDateString(new Date(value)) : 'N/A'
    },
  }),
  columnHelper.accessor('reviewed_at', {
    header: 'Reviewed',
    cell: (info) => {
      const value = info.getValue()
      return value ? getLocaleDateString(new Date(value)) : 'N/A'
    },
  }),
  columnHelper.accessor('reviewed_by_name', {
    header: 'Reviewer',
    cell: (info) => info.getValue() || 'N/A',
  }),
  columnHelper.accessor('review_notes', {
    header: 'Reason',
    cell: (info) => {
      return (
        <DisplayTextOnHoverTooltip
          text={info.getValue() || 'No reason provided'}
          classNames='max-w-60'
        />
      )
    }
  }),
]

const RejectedSubstantiationsTableImpl: React.FC<RejectedSubstantiationsTableProps> = ({ substantiationId }) => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 5,
  })

  const {
    data: rejectedSubstantiations,
    isLoading,
    isError,
  } = useGetAdminSubstantiationsSubstantiationIdRejectedSubstantiations(
    substantiationId,
    {
      page: pagination.pageIndex + 1, // Convert 0-based to 1-based page indexing
      items: pagination.pageSize,
    }
  ) as {
    data?: { data: AdminSubstantiation[]; pagination: PaginationMetadata } | undefined
    isLoading: boolean
    isError: boolean
  }

  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()

  const [isOpen, setIsOpen] = useState(false)

  const table = useReactTable({
    data: rejectedSubstantiations?.data || [],
    columns: getColumns,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    state: {
      pagination,
    },
  })

  useEffect(() => {
    if (rejectedSubstantiations) {
      setPaginationMetadata(rejectedSubstantiations.pagination)
    }
  }, [rejectedSubstantiations?.pagination])

  useEffect(() => {
    if (isLoading || isError) {
      // Keep container open during loading (for skeleton) or error (for error message)
      setIsOpen(true)
    } else {
      // After loading, open only if there's data
      const hasRejectedSubstantiations = (rejectedSubstantiations?.data?.length || 0) > 0
      setIsOpen(hasRejectedSubstantiations)
    }
  }, [isLoading, isError, rejectedSubstantiations?.data?.length])

  return (
    <CollapsibleContainer heading='Rejected Substantiations' open={isOpen} onOpenChange={setIsOpen}>
      <div className='px-4 mt-4'>
        <TanstackTable
          table={table}
          isLoading={isLoading}
          isError={isError}
          paginationLabelSingular='substantiation'
          paginationMetadata={paginationMetadata}
          onNextPageClick={() => {
            if (paginationMetadata?.next_page) {
              setPagination({
                ...pagination,
                pageIndex: paginationMetadata.next_page - 1,
              })
            }
          }}
          onPreviousPageClick={() => {
            if (paginationMetadata?.prev_page) {
              setPagination({
                ...pagination,
                pageIndex: paginationMetadata.prev_page - 1,
              })
            }
          }}
        />
      </div>
    </CollapsibleContainer>
  )
}

const queryClient = new QueryClient()

const RejectedSubstantiationsTable: React.FC<RejectedSubstantiationsTableProps> = (props) => {
  return (
    <QueryClientProvider client={queryClient}>
      <RejectedSubstantiationsTableImpl {...props} />
    </QueryClientProvider>
  )
}

export default RejectedSubstantiationsTable
